import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import {
  getCacheEntry,
  setCache,
  getEventsCacheKey,
  EVENTS_CACHE_TTL,
} from "../lib/calendarCacheService";
import { CalendarEvent } from "../types/calendar";

// =============================================================================
// useCalendarData - CALENDAR EVENT FETCHER
// =============================================================================
// PURPOSE: Fetches calendar events from Supabase with localStorage caching.
//
// REQUIREMENTS:
// - Filter by organization_id, date range, user (for RBAC)
// - Handle call_booked and call_cancelled event types
// - Deduplicate: remove call_booked if call_cancelled exists for same lead+time
// - Cache results to avoid redundant fetches on re-navigation
//
// KEY DECISIONS:
// - Safety cap: 1000 calls per calendar view range
// - Orders by call_start_time ascending (chronological display)
// - Deduplication ensures only cancelled entry remains (not both)
// - Cache-first: show cached data instantly, skip network if cache hit
// - AbortController prevents race conditions on fast navigation
// =============================================================================

/**
 * Deduplicate events: drop a call_booked when a call_cancelled killed it, so
 * only the cancelled entry remains.
 *
 * Matching mirrors computeOutcomeBindings (useCalendarEnrichment): a cancel's
 * `call_id` (Calendly invitee UUID) is authoritative when present; fall back to
 * lead+call_start_time ONLY when the cancel's call_id is NULL (~2% of Rayston
 * cancels, a Calendly webhook quirk; those rows always carry call_start_time —
 * 93/93 live).
 *
 * Keying on lead+call_start_time ALONE was the same-slot-rebook bug
 * (P0-SAMESLOT-HIDE): Adrian booked Gavryll Singh at 16:40 with the wrong name
 * (call_id 392707bb), cancelled it, then rebooked the SAME 16:40 slot with a
 * fresh invite (call_id ae4a661f, live). Both rows shared lead+16:40, so the
 * old key dropped the LIVE ae4a661f too and hid the call. By call_id, the
 * cancelled set is {392707bb} and ae4a661f survives.
 *
 * G3 residual (Rayston, 2026-09-10): 0 of 7,411 (lead,slot) pairs hold 2+
 * bookings, so the NULL-call_id slot fallback drops at most one booking — no
 * same-slot ambiguity remains.
 */
export function deduplicateEvents(events: CalendarEvent[]): CalendarEvent[] {
  const slotKey = (ev: CalendarEvent) => `${ev.lead_id}|${ev.call_start_time}`;

  // A cancel with a call_id kills exactly that booking; a NULL-call_id cancel
  // kills by lead+slot (its call_start_time is always set).
  const cancelledCallIds = new Set<string>();
  const cancelledSlotKeys = new Set<string>();
  for (const ev of events) {
    if (ev.event_type !== "call_cancelled" || !ev.lead_id) continue;
    if (ev.call_id) cancelledCallIds.add(ev.call_id);
    else cancelledSlotKeys.add(slotKey(ev));
  }

  const isDropped = (ev: CalendarEvent) =>
    ev.event_type === "call_booked" &&
    !!ev.lead_id &&
    ((!!ev.call_id && cancelledCallIds.has(ev.call_id)) || cancelledSlotKeys.has(slotKey(ev)));

  // Capture the calendar name from each booked row ABOUT TO BE DROPPED and
  // stamp it onto the surviving cancel row as `booked_call_type` (a cancel's
  // own `status` is uniformly "cancelled" with no calendar name). Keyed BOTH by
  // call_id and by lead+slot so a call_id cancel and a NULL-call_id cancel can
  // each recover it.
  const typeByCallId = new Map<string, string>();
  const typeBySlot = new Map<string, string>();
  for (const ev of events) {
    if (!isDropped(ev) || !ev.status) continue;
    const s = ev.status.trim();
    if (!s || s.toLowerCase() === "cancelled") continue;
    if (ev.call_id) typeByCallId.set(ev.call_id, s);
    typeBySlot.set(slotKey(ev), s);
  }

  return events
    .filter((ev) => !isDropped(ev))
    .map((ev) => {
      if (ev.event_type === "call_cancelled" && ev.lead_id) {
        const recovered =
          (ev.call_id && typeByCallId.get(ev.call_id)) || typeBySlot.get(slotKey(ev));
        if (recovered) return { ...ev, booked_call_type: recovered };
      }
      return ev;
    });
}

const REFRESH_DEBOUNCE_MS = 1500;
const VISIBLE_POLL_MS = 2 * 60 * 1000;
const REVALIDATE_AFTER_MS = 30 * 1000;

export const useCalendarData = (orgId: string | null, rangeStart: Date, rangeEnd: Date) => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  // Only the newest request may write state. A background refresh can still be
  // in flight when the user pages to another week; its rows must not land.
  const requestSeqRef = useRef(0);
  // JSON of the list currently in state, to skip no-op background writes.
  const lastJsonRef = useRef<string>("");

  const startISO = useMemo(() => rangeStart.toISOString(), [rangeStart]);
  const endISO = useMemo(() => rangeEnd.toISOString(), [rangeEnd]);

  // `force` = a background refresh of an already-rendered range (a Calendly
  // booking/cancel landed while the tab was open). It skips the cache read,
  // because the cache still holds the pre-cancel list for up to
  // EVENTS_CACHE_TTL, and it stays silent: no spinner over a grid that is
  // already on screen. Without this the list only reloaded on mount or range
  // change, so a cancel that arrived mid-session left the booked chip alive
  // in Live mode until the rep navigated away (Ashton Joash Choo, 2026-10-12:
  // both Oct 14 bookings cancelled 06:31 SGT, still "Missing pre-call").
  const fetchEvents = useCallback(
    async (force = false) => {
      if (!supabase || !orgId) return;
      const seq = ++requestSeqRef.current;
      const isLatest = () => isMountedRef.current && seq === requestSeqRef.current;

      const cacheKey = getEventsCacheKey(orgId, startISO, endISO);
      // Silent = never show the spinner or an error over a grid on screen.
      let silent = force;
      if (!force) {
        const hit = getCacheEntry<CalendarEvent[]>(cacheKey, EVENTS_CACHE_TTL, orgId);
        if (hit) {
          // Paint the cache instantly. A range that is not over yet can still
          // gain cancels, so a cache older than REVALIDATE_AFTER_MS is then
          // re-checked silently (stale-while-revalidate). Past ranges keep
          // their 24h tier: a call that already happened does not get
          // cancelled, and a reload still clears them (calendarCacheService).
          setEvents(hit.data);
          lastJsonRef.current = JSON.stringify(hit.data);
          setLoading(false);
          const rangeIsLive = Date.parse(endISO) >= Date.now();
          if (!rangeIsLive || Date.now() - hit.timestamp < REVALIDATE_AFTER_MS) return;
          silent = true;
        } else {
          setLoading(true);
        }
      }

      setError(null);

      try {
        // Paginate to fetch ALL events in range. A single .limit(1000) silently
        // truncated busy months — the May 2026 grid holds 1378 call rows, so every
        // date past row 1000 (the 19th onward) vanished. PostgREST caps any one
        // response at PGRST_MAX_ROWS=1000, so loop .range() until a page returns
        // < PAGE_SIZE. The .order("id") tiebreaker is mandatory: ~89% of rows share
        // a call_start_time with another row, so without a deterministic secondary
        // sort the page boundary would skip/duplicate rows. Mirrors useCalendarEnrichment.
        const PAGE_SIZE = 1000;
        const allRows: any[] = [];
        let from = 0;
        while (true) {
          const { data, error: fetchError } = await supabase
            .from("transactions")
            .select(
              `
          id,
          call_id,
          call_start_time,
          call_booked_time,
          event_date,
          event_type,
          status,
          calendly_event_type_id,
          lead_id,
          closer_id,
          setter_id,
          call_cancel_by,
          call_cancel_reason,
          organization_id,
          leads:lead_id ( name, email ),
          closer:closer_id ( full_name ),
          setter:setter_id ( full_name )
        `
            )
            .eq("organization_id", orgId)
            .in("event_type", ["call_booked", "call_cancelled"])
            .not("call_start_time", "is", null)
            .gte("call_start_time", startISO)
            .lte("call_start_time", endISO)
            .order("call_start_time", { ascending: true })
            .order("id", { ascending: true })
            .range(from, from + PAGE_SIZE - 1);

          if (fetchError) throw fetchError;
          const rows = data || [];
          allRows.push(...rows);
          if (rows.length < PAGE_SIZE) break;
          from += PAGE_SIZE;
        }

        if (!isLatest()) return;

        const mapped: CalendarEvent[] = allRows.map((row: any) => ({
          id: row.id,
          call_id: row.call_id,
          call_start_time: row.call_start_time,
          call_booked_time: row.call_booked_time,
          event_date: row.event_date,
          event_type: row.event_type,
          status: row.status,
          calendly_event_type_id: row.calendly_event_type_id ?? null,
          lead_id: row.lead_id,
          lead_name: row.leads?.name || null,
          lead_email: row.leads?.email || null,
          closer_id: row.closer_id,
          closer_name: row.closer?.full_name || null,
          setter_id: row.setter_id,
          setter_name: row.setter?.full_name || null,
          call_cancel_by: row.call_cancel_by,
          call_cancel_reason: row.call_cancel_reason,
          organization_id: row.organization_id,
        }));

        const deduplicated = deduplicateEvents(mapped);
        // Most background refreshes find nothing new. Skip the state write
        // then, so the grid and its enrichment do not re-render every 2 min.
        const json = JSON.stringify(deduplicated);
        if (json !== lastJsonRef.current) {
          lastJsonRef.current = json;
          setEvents(deduplicated);
        }

        // Cache for next visit
        setCache(cacheKey, deduplicated, orgId);
      } catch (err: unknown) {
        if (!isLatest()) return;
        logger.error("Failed to fetch calendar events:", err);
        // A failed background refresh keeps the grid it already has.
        if (!silent) setError(err instanceof Error ? err.message : "Failed to load calendar data");
      } finally {
        if (isLatest()) setLoading(false);
      }
    },
    [orgId, startISO, endISO]
  );

  useEffect(() => {
    isMountedRef.current = true;
    fetchEvents(false);
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchEvents]);

  // Debounced silent refresh. A Calendly cancel or reschedule lands as a
  // burst (Ashton: two cancels 14s apart), so 1.5s coalesces a burst into
  // one paginated fetch. The timer reads fetchEvents through a ref: a closure
  // would keep the range on screen when it was scheduled, and paint that old
  // week if the user paged inside the window.
  const fetchEventsRef = useRef(fetchEvents);
  fetchEventsRef.current = fetchEvents;
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      fetchEventsRef.current(true);
    }, REFRESH_DEBOUNCE_MS);
  }, []);

  // Every screen built on this hook (Calendar page, rep-portal mini calendar,
  // dashboard upcoming calls) stays fresh on its own:
  // - tab comes back into view -> refresh (the tab may have slept).
  // - tab on screen -> refresh every 2 min. Realtime can drop a message while
  //   still reporting "connected", and nothing else would catch that.
  // Hidden tabs never poll. Cost measured 2026-10-13: ~2 ms per query on the
  // (organization_id, call_start_time) index, 185 rows for 17 days.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") scheduleRefresh();
    }, VISIBLE_POLL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [scheduleRefresh]);

  return { events, loading, error, refetch: fetchEvents, scheduleRefresh };
};
